<template>
  <!--
    Portfolio summary card (variant 1E). One card shape throughout, split into
    two labelled groups: TOTALS and BY ACCOUNT TYPE. The panel is an always-dark
    brand surface, so its colours don't flip with the theme.

    Breakpoints follow the design: mobile < 768, tablet 768–1119, desktop ≥ 1120
    (the 1120 stop has no Tailwind screen, hence the arbitrary min-[1120px]).
  -->
  <section
    class="mx-auto flex w-full max-w-[1180px] flex-col gap-3.5 overflow-hidden rounded-[20px] border border-white/10 border-t-secondary-5 bg-primary-7 p-4 text-white [border-top-width:2px] md:gap-[22px] md:rounded-[26px] md:border-x-0 md:border-b-0 md:px-6 md:pb-6 md:pt-[26px] md:[border-top-width:3px] min-[1120px]:px-8 min-[1120px]:pb-6 min-[1120px]:pt-8"
  >
    <template v-if="hasActivePortfolio">
      <HeroHeader
        :total="totalActivePortfolioValue"
        @add-investment="navigateToInvest('dashboard_header')"
      />

      <!-- Mobile CTA. Desktop/tablet get the button inline in HeroHeader
      instead, where the header row has room for it. The section's flex gap
      already gives every child 14px of space; -my-1.5 pulls 6px off each
      side so this one lands on the requested 8px instead of stacking on
      top of it. -->
      <Button
        @click="navigateToInvest('dashboard_header')"
        icon
        label
        size="sm"
        variant="always-secondary-neo"
        class="-mt-1.5 mb-1 w-full justify-center md:hidden"
      >
        <template #icon>
          <Icon name="TrendingUp" :stroke-width="2" class="h-6 w-6" />
        </template>
        <template #label>{{ PORTFOLIO_TERMS.addInvestment }}</template>
      </Button>

      <!-- TOTALS — tablet/desktop. 2-up on tablet, 4-up on desktop. -->
      <div class="hidden flex-col gap-2.5 md:flex">
        <h4 :class="groupLabelClass">{{ PORTFOLIO_TERMS.totalsGroup }}</h4>
        <div class="grid grid-cols-2 gap-3 min-[1120px]:grid-cols-4">
          <PortfolioTotalTile
            v-for="tile in totalTiles"
            :key="tile.key"
            :label="tile.label"
            :value="tile.value"
            :info="tile.info"
            :date="tile.date"
            :accent="tile.accent"
            :links="tile.links"
            @link-click="handlePortfolioLinkClick"
          />
        </div>
      </div>

      <!-- TOTALS — mobile. The same four figures, as stacked rows in two
      blocks: the running totals, then the next scheduled payment. -->
      <div class="flex flex-col gap-3.5 md:hidden">
        <div class="flex flex-col gap-1 rounded-2xl bg-primary-6 p-4">
          <div
            v-for="row in mobileTotalRows"
            :key="row.label"
            class="flex items-baseline justify-between gap-3"
          >
            <span class="font-inter text-[15px] text-primary-1">{{ row.label }}</span>
            <span class="text-right font-inter text-[15px] font-medium text-white">
              {{ row.value }}
            </span>
          </div>
          <p
            v-if="portfolioSummary.principalSince"
            class="pt-1.5 font-inter text-sm text-primary-2"
          >
            {{ portfolioSummary.principalSince }}
          </p>
        </div>

        <div class="flex flex-col gap-0 rounded-2xl bg-primary-6 p-4">
          <div class="flex items-baseline justify-between gap-3">
            <span class="font-inter text-[15px] text-primary-1">
              {{ PORTFOLIO_TERMS.nextMonthlyPayment }}
            </span>
            <span class="text-right font-inter text-[15px] font-medium text-white">
              {{ portfolioSummary.nextEarnings }}
            </span>
          </div>
          <p
            v-if="portfolioSummary.nextEarningsOn"
            class="font-inter text-sm font-medium text-green-400"
          >
            {{ portfolioSummary.nextEarningsOn }}
          </p>
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1 pt-2">
            <button
              type="button"
              class="font-inter text-sm text-primary-1"
              @click="handlePortfolioLinkClick('view-all-earnings')"
            >
              {{ PORTFOLIO_TERMS.viewAllEarnings }}
            </button>
            <span aria-hidden="true" class="font-inter text-[11px] text-primary-2">●</span>
            <button
              type="button"
              class="font-inter text-sm text-primary-1"
              @click="handlePortfolioLinkClick('view-breakdown')"
            >
              {{ PORTFOLIO_TERMS.viewBreakdown }}
            </button>
          </div>
        </div>
      </div>

      <!-- BY ACCOUNT TYPE — the cards stretch to fill the row: the column count
      equals the number of account types held (tablet caps at 2, so four types
      wrap 2×2 instead of squeezing into one row). -->
      <div class="flex flex-col gap-2.5">
        <h4 :class="[groupLabelClass, 'hidden md:block']">
          {{ PORTFOLIO_TERMS.accountTypeGroup }}
        </h4>

        <div class="flex flex-col gap-2.5 md:hidden">
          <InvestmentTypeCard
            v-for="group in accountGroups"
            :key="group.key"
            :group="group"
            density="accordion"
          />
        </div>

        <div
          class="hidden gap-3 md:grid md:[grid-template-columns:repeat(var(--acct-cols-tablet),minmax(0,1fr))] min-[1120px]:[grid-template-columns:repeat(var(--acct-cols-desktop),minmax(0,1fr))]"
          :style="accountGridStyle"
        >
          <InvestmentTypeCard
            v-for="group in accountGroups"
            :key="group.key"
            :group="group"
            density="tile"
          />
        </div>
      </div>

      <p class="font-inter text-[12.5px] text-primary-2">{{ PORTFOLIO_TERMS.valuesAsOfToday }}</p>
    </template>

    <!-- No investments empty state -->
    <div v-else class="mb-5 mt-4 md:mb-12">
      <div>
        <h2 class="text-2xl md:text-3xl font-light tracking-[-0.96] text-left text-[#ffffff]">
          {{ PORTFOLIO_TERMS.emptyStateHeadlinePrefix }}
          <span class="font-bold text-secondary-5"
            >{{ PORTFOLIO_TERMS.emptyStateHeadlineHighlight }}
          </span>
          {{ PORTFOLIO_TERMS.emptyStateHeadlineSuffix }}
        </h2>
        <p class="flex opacity-[0.6] text-sm md:text-lg tracking-[-0.48] text-left text-[#ffffff]">
          {{ PORTFOLIO_TERMS.emptyStateBody }}
        </p>
      </div>
      <Button
        @click="navigateToInvest('dashboard_empty_state')"
        icon
        label
        size="lg"
        variant="always-secondary-neo"
        class="mt-8 z-10"
      >
        <template #icon>
          <Icon name="TrendingUp" :stroke-width="2" class="h-6 w-6" />
        </template>
        <template #label>{{ PORTFOLIO_TERMS.startInvesting }}</template>
      </Button>
    </div>
  </section>

  <EarningsBreakdownModal
    :open="isEarningsBreakdownOpen"
    @close="isEarningsBreakdownOpen = false"
  />
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { Button, Icon, EarningsBreakdownModal, InvestmentTypeCard } from "@/components";
import { useInvestmentStore } from "@/stores";
import { useAnalytics } from "@/composables/useAnalytics";
import { PORTFOLIO_TERMS, PORTFOLIO_INFO } from "@/constants/portfolioTerms";
import HeroHeader from "./HeroHeader.vue";
import PortfolioTotalTile from "./PortfolioTotalTile.vue";

const router = useRouter();
const investmentStore = useInvestmentStore();
const { makeInvestmentClicked } = useAnalytics();

function navigateToInvest(source) {
  makeInvestmentClicked(source);
  router.push("/make-an-investment");
}

const isEarningsBreakdownOpen = ref(false);

const portfolioSummary = computed(() => investmentStore.getPortfolioSummary());
const accountGroups = computed(() => investmentStore.getPortfolioAccountCards());
const totalActivePortfolioValue = computed(() => investmentStore.getTotalActivePortfolioValue());
const hasActivePortfolio = computed(() => accountGroups.value.length > 0);

const groupLabelClass =
  "font-inter text-[10px] font-medium uppercase tracking-[0.16em] text-primary-1";

const totalTiles = computed(() => [
  {
    key: "principal",
    label: PORTFOLIO_TERMS.principalAmount,
    value: portfolioSummary.value.principal,
    info: PORTFOLIO_INFO.principalAmount,
  },
  {
    key: "accrued",
    label: PORTFOLIO_TERMS.totalAccruedInterest,
    value: portfolioSummary.value.accruedInterest,
    info: PORTFOLIO_INFO.totalAccruedInterest,
  },
  {
    key: "monthly",
    label: PORTFOLIO_TERMS.totalMonthlyPayments,
    value: portfolioSummary.value.monthlyPayments,
    info: PORTFOLIO_INFO.totalMonthlyPayments,
  },
  {
    key: "next",
    label: PORTFOLIO_TERMS.nextMonthlyPayment,
    value: portfolioSummary.value.nextEarnings,
    info: PORTFOLIO_INFO.nextMonthlyPayment,
    date: portfolioSummary.value.nextEarningsDate,
    accent: true,
    links: true,
  },
]);

const mobileTotalRows = computed(() => [
  { label: PORTFOLIO_TERMS.principalAmount, value: portfolioSummary.value.principal },
  { label: PORTFOLIO_TERMS.totalAccruedInterest, value: portfolioSummary.value.accruedInterest },
  { label: PORTFOLIO_TERMS.totalMonthlyPayments, value: portfolioSummary.value.monthlyPayments },
]);

// Drives the stretch: one column per account type held. Tablet has room for
// two full-width cards, so four types wrap to 2×2 rather than shrinking.
const accountGridStyle = computed(() => {
  const count = accountGroups.value.length;
  return {
    "--acct-cols-tablet": count === 4 ? 2 : count,
    "--acct-cols-desktop": count,
  };
});

function handlePortfolioLinkClick(action) {
  if (action === "view-all-earnings") {
    router.push("/portfolio?selectedTab=earnings");
  } else if (action === "view-breakdown") {
    isEarningsBreakdownOpen.value = true;
  }
}
</script>
